import { DispatchCard } from 'src/entities/dispatch';

import { OverloadFlags } from './vehicleAlertReasons';

export type LoadTotals = { cases: number; kg: number; m3: number };

export const ZERO_LOAD: LoadTotals = { cases: 0, kg: 0, m3: 0 };

/** 積載の上限。truck マスタの値が無ければ null（= キャパ不明で超過判定の対象外） */
export type CapacityLike = {
  maxCapacity?: number | null;
  maxCapacityKg?: number | null;
  maxCapacityM3?: number | null;
};

/** 積載量の計算に要る 1 カード分。`VehicleRowVM['cards']` と構造互換 */
export type PeakLoadSourceCard = {
  operations: readonly {
    action: '積' | '降';
    arrivalAt: string;
    departureAt: string;
    shipmentOrderLegRunId: number;
  }[];
  legRuns: readonly {
    id?: number | null;
    item_count: number;
    item_total_weight_kg: number;
    item_total_volume_m3: number;
  }[];
};

/** 積載量の計算に要る 1 回転行分。`RotationRow` と構造互換 */
export type PeakLoadRow = {
  rowId: string;
  cards: readonly DispatchCard[];
  source: { cards: readonly PeakLoadSourceCard[] };
};

/** 積載量の計算に要る 1 勤務分。`VehicleRowGroup` と構造互換 */
export type PeakLoadGroup = { rows: readonly PeakLoadRow[] };

const addLoad = (a: LoadTotals, b: LoadTotals): LoadTotals => ({
  cases: a.cases + b.cases,
  kg: a.kg + b.kg,
  m3: a.m3 + b.m3,
});

const subLoad = (a: LoadTotals, b: LoadTotals): LoadTotals => ({
  cases: a.cases - b.cases,
  kg: a.kg - b.kg,
  m3: a.m3 - b.m3,
});

const maxLoad = (a: LoadTotals, b: LoadTotals): LoadTotals => ({
  cases: Math.max(a.cases, b.cases),
  kg: Math.max(a.kg, b.kg),
  m3: Math.max(a.m3, b.m3),
});

/**
 * 勤務ごとの結果を rows 配列の同一性で使い回す。
 *
 * Why: 返す Map は毎回新しいので、値まで作り直すと 1 台に回転を足しただけで全車両の
 * `load` の参照が変わり、行と車両カードの memo が全部破れる（rerenderRotationAdd /
 * rerenderVehicleListRows が固定している性質）。adapter が変わっていないグループの
 * rows 配列を使い回すので、参照が同じなら中身も同じ。WeakMap なので古い配列は
 * そのまま回収される。
 */
const peakCache = new WeakMap<object, Map<string, LoadTotals>>();

type LoadEvent = {
  rowId: string;
  action: '積' | '降';
  arrivalAt: string;
  departureAt: string;
  legRunId: number;
};

/**
 * 回転行ごとの積載量（= その回転で同時に積んでいる量の最大）。キーは `RotationRow.rowId`。
 *
 * 入力は**絞り込み前**の全グループ。回転や選択で絞ったあとの行を渡すと、隠れている回転で
 * 積んだぶんが消えて表示中の行の積載量まで変わる（絞り込みは見え方だけを変えるべき）。
 *
 * 旧地図 /plans の移植。`models/Operations.ts` が **勤務 (shift_truck) 全体**の operation を
 * 到着順に見て「積」で加算・「降」で減算した途中の値を各 operation に持たせ、
 * `PlanningsOperationCycle.maxWeightG` がその回転に属する operation の最大を採る。
 * ここでも同じ順序で勤務全体を 1 度だけ走査し、行ごとに最大を拾う。
 *
 * Why not 行ごとに閉じて計算する: 積んだ回転と降ろす回転が違う案件を取りこぼす。
 * 回転 1 で A を積み、回転 2 で B を積んで降ろし、回転 3 で A を降ろす勤務では、
 * 回転 2 に A の operation が 1 つも無いため、行に閉じると A のぶんが消えて
 * 回転 2 のピークが過小になる（旧実装は A を載せたまま数える）。
 *
 * 「降」は降ろす前の量もピークの候補にする。軸ごとに最大を採るのも旧実装と同じ
 * （重量のピークと体積のピークが別の地点で起きる日がある）。
 *
 * 勤務の中に「積」が無い案件は、前日から積んだままその勤務が始まったものとして
 * 開始時点の積載に足す（旧実装の initialStock と同じ扱い: `models/Operations.ts`）。
 *
 * Why not カード単位で数える: `groupOpsToCards` は同地点・同 action の operation を非連続でも
 * 1 カードにマージするため、同じ積地への再訪はカードでは区別できない（`buildOperationPlaces`
 * の注記）。log_web_issues#309 の車両はまさに同じ積地へ 2 回入っており、カードからは
 * 1 回目と 2 回目の積み分を分けられない。
 */
export function buildPeakLoadByRowId(
  rowGroups: readonly PeakLoadGroup[],
): Map<string, LoadTotals> {
  const peakByRowId = new Map<string, LoadTotals>();
  for (const group of rowGroups) {
    let cached = peakCache.get(group.rows);
    if (!cached) {
      cached = new Map<string, LoadTotals>();
      collectGroupPeaks(group.rows, cached);
      peakCache.set(group.rows, cached);
    }
    for (const [rowId, load] of cached) peakByRowId.set(rowId, load);
  }
  return peakByRowId;
}

function collectGroupPeaks(
  rows: readonly PeakLoadRow[],
  peakByRowId: Map<string, LoadTotals>,
): void {
  const loadByLegRunId = new Map<number, LoadTotals>();
  for (const row of rows) {
    for (const source of row.source.cards) {
      for (const legRun of source.legRuns) {
        if (legRun.id == null || loadByLegRunId.has(legRun.id)) continue;
        loadByLegRunId.set(legRun.id, {
          cases: legRun.item_count ?? 0,
          kg: legRun.item_total_weight_kg ?? 0,
          m3: legRun.item_total_volume_m3 ?? 0,
        });
      }
    }
  }

  const events: LoadEvent[] = [];
  for (const row of rows) {
    for (const source of row.source.cards) {
      for (const operation of source.operations) {
        if (!loadByLegRunId.has(operation.shipmentOrderLegRunId)) continue;
        events.push({
          rowId: row.rowId,
          action: operation.action,
          arrivalAt: operation.arrivalAt,
          departureAt: operation.departureAt,
          legRunId: operation.shipmentOrderLegRunId,
        });
      }
    }
  }

  // D&D で移した直後は、その案件の降地カードが override で移動元から消える
  // （Presenter.tsx の handleDragEnd）。「サーバには降ろす operation があるのに、**その行**の
  // 配車済み降地カードに残っていない」案件を勤務全体から外すことで、再取得を待たずに
  // 移動元のバーが下がる。
  //
  // 行ごとに見るのが肝。勤務全体で「どこかに居るか」を見ると、同じ勤務の別の回転へ
  // 移したときに外れない。移動先には降地カードが必ず生まれるため（同じ地点に配車済みの
  // カードがあれば status: 'matched' のまま legRunIds に追加され、無ければ
  // status: 'template' の新カードができる。placeLegRunInRow 参照）。
  //
  // 積地カードは根拠にしない。降地カードが消えても legRunIds を持ったまま残るため。
  const assignedByRowId = new Map<string, Set<number>>();
  for (const row of rows) {
    const legRunIds = new Set<number>();
    for (const card of row.cards) {
      if (card.type !== 'delivery' || card.status !== 'matched') continue;
      for (const legRunId of card.legRunIds ?? []) legRunIds.add(legRunId);
    }
    assignedByRowId.set(row.rowId, legRunIds);
  }
  const unassignedLegRunIds = new Set(
    events
      .filter((event) => event.action === '降' && !assignedByRowId.get(event.rowId)?.has(event.legRunId))
      .map((event) => event.legRunId),
  );
  const placed = unassignedLegRunIds.size === 0
    ? events
    : events.filter((event) => !unassignedLegRunIds.has(event.legRunId));

  // 到着順。同着は出発順（旧実装 models/Operations.ts と同じ並べ方）
  const sorted = [...placed].sort((a, b) => {
    const byArrival = new Date(a.arrivalAt).getTime() - new Date(b.arrivalAt).getTime();
    if (byArrival !== 0) return byArrival;
    return new Date(a.departureAt).getTime() - new Date(b.departureAt).getTime();
  });

  const loadedLegRunIds = new Set(sorted.filter((e) => e.action === '積').map((e) => e.legRunId));
  let current = sorted
    .filter((e) => e.action === '降' && !loadedLegRunIds.has(e.legRunId))
    .reduce((total, e) => addLoad(total, loadByLegRunId.get(e.legRunId) ?? ZERO_LOAD), ZERO_LOAD);

  for (const row of rows) peakByRowId.set(row.rowId, ZERO_LOAD);
  for (const event of sorted) {
    const load = loadByLegRunId.get(event.legRunId) ?? ZERO_LOAD;
    if (event.action === '積') current = addLoad(current, load);
    // 「積」は積んだ後、「降」は降ろす前の量がその operation の積載量
    peakByRowId.set(event.rowId, maxLoad(peakByRowId.get(event.rowId) ?? ZERO_LOAD, current));
    if (event.action === '降') current = subLoad(current, load);
  }

  // 除外後の sorted を渡す。除外前を渡すと、いったん外した案件を再取得前に元の行へ
  // 戻したとき（古い「降」operation が残っている）に既知扱いになり、積載から消える
  addOptimisticLoads(rows, sorted, loadByLegRunId, peakByRowId);
}

/**
 * D&D で置いた直後の案件を、置かれた行の積載量に足す。
 *
 * 置いた案件はサーバに operation がまだ無いので、上の走査には出てこない。足さないと
 * 移動先のバーも警告バッジも再取得まで動かず、積載を見ながら続けて落とす場面で超過に
 * 気づけない（移動元は同時に下がるので、勤務全体では案件が消えたように見える）。
 *
 * 積む位置が分からないため、その行のピークに足す（上限側に倒す）。行のカードにあって
 * その行に**数えている**「降」operation が無い案件が対象で、量はカードの合計から
 * 数えている案件のぶんを引いて求める。同じ地点の配車済みカードへ合流するケース（`placeLegRunInRow` が
 * `status: 'matched'` のまま `legRunIds` に足す）でも、これで合流分だけを取り出せる。
 *
 * 置かれた案件が 1 つも無い行は触らない（引き算で 0 でない差を作らないため）。
 * 置かれた案件がある行の誤差は buildOverloadFlags の相対誤差で受ける。
 */
function addOptimisticLoads(
  rows: readonly PeakLoadRow[],
  events: readonly LoadEvent[],
  loadByLegRunId: ReadonlyMap<number, LoadTotals>,
  peakByRowId: Map<string, LoadTotals>,
): void {
  for (const row of rows) {
    const unloadedHere = new Set(
      events.filter((e) => e.rowId === row.rowId && e.action === '降').map((e) => e.legRunId),
    );
    let extra = ZERO_LOAD;
    for (const card of row.cards) {
      if (card.type !== 'delivery') continue;
      if (card.status !== 'matched' && card.status !== 'template') continue;
      const legRunIds = card.legRunIds ?? [];
      if (legRunIds.every((legRunId) => unloadedHere.has(legRunId))) continue;
      const known = legRunIds
        .filter((legRunId) => unloadedHere.has(legRunId))
        .reduce((total, legRunId) => addLoad(total, loadByLegRunId.get(legRunId) ?? ZERO_LOAD), ZERO_LOAD);
      const onCard: LoadTotals = {
        cases: card.todayCases ?? 0,
        kg: card.weight ?? 0,
        m3: card.volume ?? 0,
      };
      // カードの合計より operation 側が多いことは無いはずだが、量が未登録の案件が
      // 混ざると負になりうるので 0 で止める
      extra = addLoad(extra, maxLoad(ZERO_LOAD, subLoad(onCard, known)));
    }
    if (extra.cases === 0 && extra.kg === 0 && extra.m3 === 0) continue;
    peakByRowId.set(row.rowId, addLoad(peakByRowId.get(row.rowId) ?? ZERO_LOAD, extra));
  }
}

/**
 * 上限を超えているか。
 *
 * 小数（体積 m³ など）は案件ごとの値を足し込むだけで誤差が出る（0.1 + 0.2 = 0.30000000000000004）。
 * 素の `>` だと上限ぴったりの車両に超過バッジが出るので、相対誤差ぶんの余裕を持たせる。
 * 実運用の桁（kg・m³）では、この余裕で本当の超過を見逃すことはない。
 */
const over = (current: number, max: number | null | undefined): boolean =>
  max != null && max > 0 && current - max > Math.abs(max) * 1e-9;

/**
 * 積載の超過。表示項目で今どれを見ているかに関わらず 3 つとも判定する
 * （数量を見ているときに重量超過が消えると違反に気づけない）。
 *
 * 判定を VehicleInfoView に閉じ込めず関数へ出しているのは、警告バッジと
 * 「制約違反のある車両のみ」の絞り込みで同じ結果を使うため。
 */
export function buildOverloadFlags(
  vehicle: CapacityLike,
  load: LoadTotals,
): Required<OverloadFlags> {
  return {
    quantity: over(load.cases, vehicle.maxCapacity),
    weight: over(load.kg, vehicle.maxCapacityKg),
    volume: over(load.m3, vehicle.maxCapacityM3),
  };
}
